import { definePlatformSetting } from '@crest/platform-contracts/settings';
import { projectsProjectFileAllowedContentTypes, projectsProjectFileContentTypeSchema, } from '@crest/module-projects/contracts/file';
import { z } from 'zod';
const colorSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/);
export const customerPortalAccessConfigurationValueSchema = z.strictObject({
    defaultLifetimeDays: z.number().int().min(1).max(365),
    maximumLifetimeDays: z.number().int().min(1).max(365),
    maximumActiveLinksPerProject: z.number().int().min(1).max(100),
});
export const customerPortalAccessConfigurationSetting = definePlatformSetting({
    definition: {
        key: 'customer-portal.access.configuration', moduleId: 'customer-portal',
        title: 'Customer Portal access',
        helpText: 'Controls safe project-link lifetimes and active-link limits.',
        configurationDomain: 'module', valueType: 'object', schemaVersion: 1,
        codedFallback: { defaultLifetimeDays: 30, maximumLifetimeDays: 90, maximumActiveLinksPerProject: 10 },
        permittedScopes: ['platform', 'plan', 'tenant'], mergeStrategy: 'deep-merge',
        clientSafe: true, serverOnly: false, sensitive: false, tenantEditable: true,
        userOverridable: false, offlineEligible: false, safetyClass: 'protected',
    },
    valueSchema: customerPortalAccessConfigurationValueSchema,
});
export const customerPortalDisplayValueSchema = z.strictObject({
    accentColor: colorSchema,
    backgroundColor: colorSchema,
    surfaceColor: colorSchema,
    textColor: colorSchema,
    mutedTextColor: colorSchema,
    cardRadiusPx: z.number().int().min(0).max(32),
    maximumContentWidthPx: z.number().int().min(560).max(1440),
    density: z.enum(['comfortable', 'compact']),
});
export const customerPortalDisplaySetting = definePlatformSetting({
    definition: {
        key: 'customer-portal.display.project', moduleId: 'customer-portal',
        title: 'Customer Portal display',
        helpText: 'Controls portal colors, card shape, content width, and density.',
        configurationDomain: 'layout', valueType: 'object', schemaVersion: 1,
        codedFallback: {
            accentColor: '#0f766e', backgroundColor: '#f4f7f6', surfaceColor: '#ffffff',
            textColor: '#17211f', mutedTextColor: '#52606d', cardRadiusPx: 16,
            maximumContentWidthPx: 1040, density: 'comfortable',
        },
        permittedScopes: ['platform', 'plan', 'tenant'], mergeStrategy: 'deep-merge',
        clientSafe: true, serverOnly: false, sensitive: false, tenantEditable: true,
        userOverridable: false, offlineEligible: false, safetyClass: 'ordinary',
    },
    valueSchema: customerPortalDisplayValueSchema,
});
export const customerPortalCopyValueSchema = z.strictObject({
    portalTitle: z.string().trim().min(1).max(120),
    welcomeMessage: z.string().trim().min(1).max(300),
    linksPanelTitle: z.string().trim().min(1).max(120),
    rawTokenNotice: z.string().trim().min(1).max(240),
    linkLabelFieldLabel: z.string().trim().min(1).max(120),
    linkLabelPlaceholder: z.string().trim().min(1).max(160),
    createLinkAction: z.string().trim().min(1).max(80),
    revokeLinkAction: z.string().trim().min(1).max(80),
    oneTimePathInstruction: z.string().trim().min(1).max(240),
    defaultLinkLabel: z.string().trim().min(1).max(120),
    expiresLabel: z.string().trim().min(1).max(80),
    activeLinksEmpty: z.string().trim().min(1).max(240),
    linksLoadingLabel: z.string().trim().min(1).max(120),
    linksLoadFailure: z.string().trim().min(1).max(240),
    linkCreateFailure: z.string().trim().min(1).max(240),
    linkRevokeFailure: z.string().trim().min(1).max(240),
    loadingLabel: z.string().trim().min(1).max(120),
    panelFailureMessage: z.string().trim().min(1).max(240),
});
export const customerPortalCopySetting = definePlatformSetting({
    definition: {
        key: 'customer-portal.copy.project', moduleId: 'customer-portal',
        title: 'Customer Portal copy',
        helpText: 'Controls customer-facing portal and staff link-management wording.',
        configurationDomain: 'copy', valueType: 'object', schemaVersion: 1,
        codedFallback: {
            portalTitle: 'Project Portal', welcomeMessage: 'Follow your project in one secure place.',
            linksPanelTitle: 'Customer Portal links',
            rawTokenNotice: 'Raw access tokens appear once and are never stored.',
            linkLabelFieldLabel: 'Link label (optional)',
            linkLabelPlaceholder: 'Customer, adjuster, or purpose',
            createLinkAction: 'Create secure link', revokeLinkAction: 'Revoke',
            oneTimePathInstruction: 'Copy this path now. It cannot be shown again.',
            defaultLinkLabel: 'Secure project link', expiresLabel: 'Expires',
            activeLinksEmpty: 'No active Customer Portal links.',
            linksLoadingLabel: 'Loading Customer Portal links…',
            linksLoadFailure: 'Customer Portal links could not be loaded.',
            linkCreateFailure: 'The secure Customer Portal link could not be created.',
            linkRevokeFailure: 'The Customer Portal link could not be revoked.',
            loadingLabel: 'Loading your project…',
            panelFailureMessage: 'One project section could not be loaded.',
        },
        permittedScopes: ['platform', 'plan', 'tenant'], mergeStrategy: 'deep-merge',
        clientSafe: true, serverOnly: false, sensitive: false, tenantEditable: true,
        userOverridable: false, offlineEligible: false, safetyClass: 'ordinary',
    },
    valueSchema: customerPortalCopyValueSchema,
});
export const customerPortalIntakeConfigurationValueSchema = z.strictObject({
    enabled: z.boolean(),
    allowDrafts: z.boolean(),
    defaultLifetimeDays: z.number().int().min(1).max(90),
    maximumLifetimeDays: z.number().int().min(1).max(90),
    requireCustomerName: z.boolean(),
    requireContactMethod: z.boolean(),
    showDerivedSuggestions: z.boolean(),
    maximumAffectedAreas: z.number().int().min(1).max(50),
    maximumStoryCharacters: z.number().int().min(100).max(5_000),
});
export const customerPortalIntakeConfigurationSetting = definePlatformSetting({
    definition: {
        key: 'customer-portal.intake.configuration', moduleId: 'customer-portal',
        title: 'Customer Portal loss intake',
        helpText: 'Controls secure customer loss-intake behavior and bounded submission requirements.',
        configurationDomain: 'module', valueType: 'object', schemaVersion: 1,
        codedFallback: {
            enabled: true, allowDrafts: true, defaultLifetimeDays: 14, maximumLifetimeDays: 90,
            requireCustomerName: false, requireContactMethod: false,
            showDerivedSuggestions: false,
            maximumAffectedAreas: 50, maximumStoryCharacters: 5_000,
        },
        permittedScopes: ['platform', 'plan', 'tenant'], mergeStrategy: 'deep-merge',
        clientSafe: true, serverOnly: false, sensitive: false, tenantEditable: true,
        userOverridable: false, offlineEligible: false, safetyClass: 'protected',
    },
    valueSchema: customerPortalIntakeConfigurationValueSchema,
});
export const customerPortalIntakeDisplayValueSchema = z.strictObject({
    cardSize: z.enum(['small', 'medium', 'large']),
    density: z.enum(['comfortable', 'compact']),
    columns: z.union([z.literal(1), z.literal(2)]),
    fieldGrouping: z.enum(['sections', 'single-card']),
    showProgress: z.boolean(),
    showIncidentSection: z.boolean(),
    showCustomerSection: z.boolean(),
    showInsuranceSection: z.boolean(),
    showCommunicationPreferences: z.boolean(),
});
export const customerPortalIntakeDisplaySetting = definePlatformSetting({
    definition: {
        key: 'customer-portal.intake.display', moduleId: 'customer-portal',
        title: 'Customer Portal loss-intake display',
        helpText: 'Controls intake card size, density, field columns, and optional section visibility.',
        configurationDomain: 'layout', valueType: 'object', schemaVersion: 1,
        codedFallback: {
            cardSize: 'large', density: 'comfortable', columns: 2,
            fieldGrouping: 'sections', showProgress: true,
            showIncidentSection: true, showCustomerSection: true,
            showInsuranceSection: true, showCommunicationPreferences: true,
        },
        permittedScopes: ['platform', 'plan', 'tenant'], mergeStrategy: 'deep-merge',
        clientSafe: true, serverOnly: false, sensitive: false, tenantEditable: true,
        userOverridable: false, offlineEligible: false, safetyClass: 'ordinary',
    },
    valueSchema: customerPortalIntakeDisplayValueSchema,
});
export const customerPortalIntakeCopyValueSchema = z.strictObject({
    staffPanelTitle: z.string().trim().min(1).max(120),
    staffPanelHelp: z.string().trim().min(1).max(300),
    createAction: z.string().trim().min(1).max(80),
    rotateAction: z.string().trim().min(1).max(80),
    revokeAction: z.string().trim().min(1).max(80),
    acceptAction: z.string().trim().min(1).max(80),
    recipientNameLabel: z.string().trim().min(1).max(120),
    recipientEmailLabel: z.string().trim().min(1).max(120),
    recipientPhoneLabel: z.string().trim().min(1).max(120),
    requestTitleLabel: z.string().trim().min(1).max(120),
    introMessageLabel: z.string().trim().min(1).max(120),
    emptyStaffState: z.string().trim().min(1).max(240),
    staffLoadFailure: z.string().trim().min(1).max(240),
    staffMutationFailure: z.string().trim().min(1).max(240),
    oneTimePathInstruction: z.string().trim().min(1).max(240),
    formTitle: z.string().trim().min(1).max(120),
    formIntroduction: z.string().trim().min(1).max(500),
    incidentSectionTitle: z.string().trim().min(1).max(120),
    customerSectionTitle: z.string().trim().min(1).max(120),
    insuranceSectionTitle: z.string().trim().min(1).max(120),
    communicationSectionTitle: z.string().trim().min(1).max(120),
    incidentTypeLabel: z.string().trim().min(1).max(120),
    lossTimestampLabel: z.string().trim().min(1).max(120),
    causeLabel: z.string().trim().min(1).max(120),
    affectedAreasLabel: z.string().trim().min(1).max(120),
    affectedAreasHelp: z.string().trim().min(1).max(240),
    storyLabel: z.string().trim().min(1).max(120),
    customerNameLabel: z.string().trim().min(1).max(120),
    customerEmailLabel: z.string().trim().min(1).max(120),
    customerPhoneLabel: z.string().trim().min(1).max(120),
    insuranceCompanyLabel: z.string().trim().min(1).max(120),
    policyNumberLabel: z.string().trim().min(1).max(120),
    claimNumberLabel: z.string().trim().min(1).max(120),
    adjusterNameLabel: z.string().trim().min(1).max(120),
    adjusterEmailLabel: z.string().trim().min(1).max(120),
    adjusterPhoneLabel: z.string().trim().min(1).max(120),
    emailPreferenceLabel: z.string().trim().min(1).max(120),
    smsPreferenceLabel: z.string().trim().min(1).max(120),
    phonePreferenceLabel: z.string().trim().min(1).max(120),
    preferredChannelLabel: z.string().trim().min(1).max(120),
    saveDraftAction: z.string().trim().min(1).max(80),
    submitAction: z.string().trim().min(1).max(80),
    draftSavedMessage: z.string().trim().min(1).max(240),
    submittedMessage: z.string().trim().min(1).max(240),
    acceptedMessage: z.string().trim().min(1).max(240),
    loadingMessage: z.string().trim().min(1).max(120),
    unavailableMessage: z.string().trim().min(1).max(240),
    validationMessage: z.string().trim().min(1).max(240),
    conflictMessage: z.string().trim().min(1).max(240),
    retryMessage: z.string().trim().min(1).max(240),
});
export const customerPortalIntakeCopySetting = definePlatformSetting({
    definition: {
        key: 'customer-portal.copy.intake', moduleId: 'customer-portal',
        title: 'Customer Portal loss-intake copy',
        helpText: 'Controls staff and customer wording for the loss-intake workflow.',
        configurationDomain: 'copy', valueType: 'object', schemaVersion: 1,
        codedFallback: {
            staffPanelTitle: 'Customer loss intake',
            staffPanelHelp: 'Create a secure form, review the customer response, and accept verified facts.',
            createAction: 'Create intake link', rotateAction: 'Rotate link',
            revokeAction: 'Revoke', acceptAction: 'Accept intake',
            recipientNameLabel: 'Recipient name', recipientEmailLabel: 'Recipient email',
            recipientPhoneLabel: 'Recipient phone', requestTitleLabel: 'Request title',
            introMessageLabel: 'Introduction', emptyStaffState: 'No loss-intake requests for this project.',
            staffLoadFailure: 'Loss-intake requests could not be loaded.',
            staffMutationFailure: 'The loss-intake change could not be saved.',
            oneTimePathInstruction: 'Copy this secure path now. It cannot be shown again.',
            formTitle: 'Tell us about the loss',
            formIntroduction: 'Share what happened so the project team can prepare for the next step.',
            incidentSectionTitle: 'Loss details', customerSectionTitle: 'Your contact information',
            insuranceSectionTitle: 'Insurance information',
            communicationSectionTitle: 'Communication preferences',
            incidentTypeLabel: 'Type of incident', lossTimestampLabel: 'When did it happen?',
            causeLabel: 'Known or suspected cause', affectedAreasLabel: 'Affected areas',
            affectedAreasHelp: 'Separate multiple areas with commas.', storyLabel: 'What happened?',
            customerNameLabel: 'Name', customerEmailLabel: 'Email', customerPhoneLabel: 'Phone',
            insuranceCompanyLabel: 'Insurance company', policyNumberLabel: 'Policy number',
            claimNumberLabel: 'Claim number', adjusterNameLabel: 'Adjuster name',
            adjusterEmailLabel: 'Adjuster email', adjusterPhoneLabel: 'Adjuster phone',
            emailPreferenceLabel: 'Email', smsPreferenceLabel: 'Text message',
            phonePreferenceLabel: 'Phone call', preferredChannelLabel: 'Preferred contact method',
            saveDraftAction: 'Save draft', submitAction: 'Submit intake',
            draftSavedMessage: 'Your draft is saved.',
            submittedMessage: 'Your intake was submitted successfully.',
            acceptedMessage: 'This intake has been accepted by the project team.',
            loadingMessage: 'Loading the secure intake form…',
            unavailableMessage: 'This intake link is not available.',
            validationMessage: 'Review the highlighted information and try again.',
            conflictMessage: 'This intake changed after it was loaded. Refresh before trying again.',
            retryMessage: 'The change did not save. Your information is still here; try again.',
        },
        permittedScopes: ['platform', 'plan', 'tenant'], mergeStrategy: 'deep-merge',
        clientSafe: true, serverOnly: false, sensitive: false, tenantEditable: true,
        userOverridable: false, offlineEligible: false, safetyClass: 'ordinary',
    },
    valueSchema: customerPortalIntakeCopyValueSchema,
});
export const customerPortalIntakeAssetConfigurationValueSchema = z.strictObject({
    enabled: z.boolean(),
    maximumAssets: z.number().int().min(1).max(200),
    standardUploadMaximumBytes: z.number().int().min(6 * 1_024 * 1_024)
        .max(100 * 1_024 * 1_024),
    resumableRequiredAboveBytes: z.number().int().min(6 * 1_024 * 1_024)
        .max(100 * 1_024 * 1_024),
    maximumFileSizeBytes: z.number().int().min(1_024)
        .max(5 * 1_024 * 1_024 * 1_024),
    uploadAuthorizationSeconds: z.number().int().min(60).max(7_200),
    allowedContentTypes: z.array(projectsProjectFileContentTypeSchema).min(1).max(20).superRefine((contentTypes, context) => {
        if (new Set(contentTypes).size !== contentTypes.length) {
            context.addIssue({ code: 'custom', message: 'Intake asset content types must be unique.' });
        }
    }),
    allowDeleteBeforeAcceptance: z.boolean(),
}).superRefine((value, context) => {
    if (value.resumableRequiredAboveBytes > value.standardUploadMaximumBytes) {
        context.addIssue({
            code: 'custom',
            path: ['resumableRequiredAboveBytes'],
            message: 'The resumable threshold cannot exceed the standard upload ceiling.',
        });
    }
    if (value.standardUploadMaximumBytes > value.maximumFileSizeBytes) {
        context.addIssue({
            code: 'custom',
            path: ['standardUploadMaximumBytes'],
            message: 'The standard upload ceiling cannot exceed the maximum file size.',
        });
    }
});
export const customerPortalIntakeAssetConfigurationSetting = definePlatformSetting({
    definition: {
        key: 'customer-portal.intake-assets.configuration', moduleId: 'customer-portal',
        title: 'Customer Portal intake assets',
        helpText: 'Controls public loss-intake attachment limits, transports, types, and deletion.',
        configurationDomain: 'module', valueType: 'object', schemaVersion: 1,
        codedFallback: {
            enabled: true,
            maximumAssets: 50,
            standardUploadMaximumBytes: 100 * 1_024 * 1_024,
            resumableRequiredAboveBytes: 6 * 1_024 * 1_024,
            maximumFileSizeBytes: 5 * 1_024 * 1_024 * 1_024,
            uploadAuthorizationSeconds: 900,
            allowedContentTypes: [...projectsProjectFileAllowedContentTypes],
            allowDeleteBeforeAcceptance: true,
        },
        permittedScopes: ['platform', 'plan', 'tenant'], mergeStrategy: 'deep-merge',
        clientSafe: true, serverOnly: false, sensitive: false, tenantEditable: true,
        userOverridable: false, offlineEligible: false, safetyClass: 'protected',
    },
    valueSchema: customerPortalIntakeAssetConfigurationValueSchema,
});
export const customerPortalIntakeAssetDisplayValueSchema = z.strictObject({
    cardSize: z.enum(['small', 'medium', 'large']),
    density: z.enum(['comfortable', 'compact']),
    columns: z.union([z.literal(1), z.literal(2)]),
    showUploadProgress: z.boolean(),
    showFileSize: z.boolean(),
    showFileCategory: z.boolean(),
});
export const customerPortalIntakeAssetDisplaySetting = definePlatformSetting({
    definition: {
        key: 'customer-portal.intake-assets.display', moduleId: 'customer-portal',
        title: 'Customer Portal intake asset display',
        helpText: 'Controls attachment card size, density, columns, progress, and metadata.',
        configurationDomain: 'layout', valueType: 'object', schemaVersion: 1,
        codedFallback: {
            cardSize: 'medium', density: 'comfortable', columns: 1,
            showUploadProgress: true, showFileSize: true, showFileCategory: true,
        },
        permittedScopes: ['platform', 'plan', 'tenant'], mergeStrategy: 'deep-merge',
        clientSafe: true, serverOnly: false, sensitive: false, tenantEditable: true,
        userOverridable: false, offlineEligible: false, safetyClass: 'ordinary',
    },
    valueSchema: customerPortalIntakeAssetDisplayValueSchema,
});
export const customerPortalIntakeAssetCopyValueSchema = z.strictObject({
    sectionTitle: z.string().trim().min(1).max(120),
    sectionHelp: z.string().trim().min(1).max(300),
    selectFileLabel: z.string().trim().min(1).max(120),
    uploadAction: z.string().trim().min(1).max(80),
    deleteAction: z.string().trim().min(1).max(80),
    retryAction: z.string().trim().min(1).max(80),
    loadingMessage: z.string().trim().min(1).max(160),
    unavailableMessage: z.string().trim().min(1).max(240),
    hashingStatus: z.string().trim().min(1).max(160),
    uploadingStatus: z.string().trim().min(1).max(160),
    finalizingStatus: z.string().trim().min(1).max(160),
    emptyState: z.string().trim().min(1).max(240),
    fileTooLargeError: z.string().trim().min(1).max(240),
    fileTypeNotAllowedError: z.string().trim().min(1).max(240),
    assetLimitError: z.string().trim().min(1).max(240),
    verificationError: z.string().trim().min(1).max(240),
    uploadCompleteMessage: z.string().trim().min(1).max(240),
    deleteCompleteMessage: z.string().trim().min(1).max(240),
    retryMessage: z.string().trim().min(1).max(240),
    stateLabels: z.strictObject({
        pending: z.string().trim().min(1).max(80),
        authorized: z.string().trim().min(1).max(80),
        verifying: z.string().trim().min(1).max(80),
        attached: z.string().trim().min(1).max(80),
        quarantined: z.string().trim().min(1).max(80),
        deleting: z.string().trim().min(1).max(80),
        deleted: z.string().trim().min(1).max(80),
        transferred: z.string().trim().min(1).max(80),
    }),
});
export const customerPortalIntakeAssetCopySetting = definePlatformSetting({
    definition: {
        key: 'customer-portal.copy.intake-assets', moduleId: 'customer-portal',
        title: 'Customer Portal intake asset copy',
        helpText: 'Controls customer-facing attachment labels, progress, errors, and retry wording.',
        configurationDomain: 'copy', valueType: 'object', schemaVersion: 1,
        codedFallback: {
            sectionTitle: 'Photos and files',
            sectionHelp: 'Add photos, videos, or documents that help explain the loss.',
            selectFileLabel: 'Choose a file', uploadAction: 'Upload file', deleteAction: 'Remove',
            retryAction: 'Try again', loadingMessage: 'Loading files…',
            unavailableMessage: 'The files could not be loaded.',
            hashingStatus: 'Preparing file…', uploadingStatus: 'Uploading file…',
            finalizingStatus: 'Checking file…', emptyState: 'No files have been added yet.',
            fileTooLargeError: 'This file is larger than the allowed size.',
            fileTypeNotAllowedError: 'This file type is not allowed.',
            assetLimitError: 'The maximum number of files has been reached.',
            verificationError: 'The uploaded file could not be verified.',
            uploadCompleteMessage: 'The file was uploaded.',
            deleteCompleteMessage: 'The file was removed.',
            retryMessage: 'The file change did not finish. Keep this page open and try again.',
            stateLabels: {
                pending: 'Preparing', authorized: 'Ready to upload', verifying: 'Checking',
                attached: 'Uploaded', quarantined: 'Needs review', deleting: 'Removing',
                deleted: 'Removed', transferred: 'Added to project',
            },
        },
        permittedScopes: ['platform', 'plan', 'tenant'], mergeStrategy: 'deep-merge',
        clientSafe: true, serverOnly: false, sensitive: false, tenantEditable: true,
        userOverridable: false, offlineEligible: false, safetyClass: 'ordinary',
    },
    valueSchema: customerPortalIntakeAssetCopyValueSchema,
});
export const customerPortalSettingRegistrations = [
    customerPortalAccessConfigurationSetting,
    customerPortalDisplaySetting,
    customerPortalCopySetting,
    customerPortalIntakeConfigurationSetting,
    customerPortalIntakeDisplaySetting,
    customerPortalIntakeCopySetting,
    customerPortalIntakeAssetConfigurationSetting,
    customerPortalIntakeAssetDisplaySetting,
    customerPortalIntakeAssetCopySetting,
];
//# sourceMappingURL=customer-portal-setting-definitions.js.map